import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  RefreshCw,
  Search,
  FileSpreadsheet,
  FileText,
  ChevronDown,
  ChevronUp,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { registrationAPI } from '@/services/api';
import { FIELD_CONFIG } from '@/constants/registrationFields';
import { formatDateTime, downloadBlob } from './adminUtils';

export default function RegistrationsPanel() {
  const { token } = useAdminAuth();
  const [registrations, setRegistrations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterIsrael, setFilterIsrael] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, countData] = await Promise.all([
        registrationAPI.listRegistrations(token),
        registrationAPI.getRegistrationCount(token),
      ]);
      setRegistrations(list);
      setTotalCount(countData.count);
    } catch (err) {
      setError(err?.detail || err?.message || 'Failed to load registrations');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let results = registrations;
    if (filterIsrael !== 'all') {
      results = results.filter((r) => r.visited_before_israel === filterIsrael);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      results = results.filter((r) => {
        const haystack = [
          r.first_name,
          r.last_name,
          r.email,
          r.passport_no,
          r.telephone_number,
          r.tourist_birth_country,
          r.country_of_present_residence,
          r.id,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    }
    return results;
  }, [registrations, searchQuery, filterIsrael]);

  const handleDownload = async (type, registrationId = null) => {
    const key = registrationId ? `${type}-${registrationId}` : `all-${type}`;
    setDownloading(key);
    try {
      let blob;
      let filename;
      if (registrationId) {
        blob = type === 'xlsx'
          ? await registrationAPI.downloadRegistrationXlsx(token, registrationId)
          : await registrationAPI.downloadRegistrationPdf(token, registrationId);
        filename = `registration-${registrationId.slice(0, 8)}.${type === 'xlsx' ? 'xlsx' : 'pdf'}`;
      } else {
        blob = type === 'xlsx'
          ? await registrationAPI.downloadAllXlsx(token)
          : await registrationAPI.downloadAllPdf(token);
        filename = `tour-registrations.${type === 'xlsx' ? 'xlsx' : 'pdf'}`;
      }
      downloadBlob(blob, filename);
    } catch (err) {
      setError(err?.detail || err?.message || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tour Registrations</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {totalCount} total · {filtered.length} shown
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDownload('xlsx')}
            disabled={!!downloading || registrations.length === 0}
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
          >
            {downloading === 'all-xlsx' ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileSpreadsheet size={14} className="mr-1.5" />}
            Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDownload('pdf')}
            disabled={!!downloading || registrations.length === 0}
            className="border-red-200 text-red-700 hover:bg-red-50"
          >
            {downloading === 'all-pdf' ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileText size={14} className="mr-1.5" />}
            PDF
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, passport, phone…"
            className="pl-9 bg-white"
          />
        </div>
        <Select value={filterIsrael} onValueChange={setFilterIsrael}>
          <SelectTrigger className="w-full sm:w-52 bg-white">
            <SelectValue placeholder="Israel visit filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All registrations</SelectItem>
            <SelectItem value="yes">Visited Israel: Yes</SelectItem>
            <SelectItem value="no">Visited Israel: No</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {loading && registrations.length === 0 ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-brand-blue-500" size={28} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <Users size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">
            {registrations.length === 0 ? 'No registrations yet.' : 'No results match your search.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((reg) => {
            const isExpanded = expandedId === reg.id;
            const name = `${reg.first_name} ${reg.last_name}`;
            return (
              <div key={reg.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
                <button
                  type="button"
                  onClick={() => setExpandedId(isExpanded ? null : reg.id)}
                  className="w-full flex items-center gap-3 p-4 sm:p-5 hover:bg-gray-50/80 transition-colors text-left"
                >
                  <div className="w-10 h-10 bg-brand-blue-50 rounded-full flex items-center justify-center flex-shrink-0 text-brand-blue-600 font-semibold text-sm">
                    {reg.first_name?.[0]}{reg.last_name?.[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-800 truncate">{name}</p>
                    <p className="text-sm text-gray-500 truncate">{reg.email}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-gray-400">
                      <span>{reg.passport_no}</span>
                      <span>{reg.telephone_number}</span>
                      <span>{formatDateTime(reg.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-emerald-600 hover:bg-emerald-50"
                      onClick={(e) => { e.stopPropagation(); handleDownload('xlsx', reg.id); }}
                      disabled={!!downloading}
                    >
                      {downloading === `xlsx-${reg.id}` ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-600 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); handleDownload('pdf', reg.id); }}
                      disabled={!!downloading}
                    >
                      {downloading === `pdf-${reg.id}` ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
                    </Button>
                    {isExpanded ? <ChevronUp size={18} className="text-gray-400 ml-1" /> : <ChevronDown size={18} className="text-gray-400 ml-1" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 sm:px-5 pb-5 border-t border-gray-50 pt-4 bg-gray-50/50">
                    <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                      {Object.keys(FIELD_CONFIG).map((key) => (
                        <div key={key}>
                          <dt className="text-gray-400 text-xs uppercase tracking-wide">{FIELD_CONFIG[key].label}</dt>
                          <dd className="text-gray-800 font-medium mt-0.5">{reg[key] || '-'}</dd>
                        </div>
                      ))}
                      <div>
                        <dt className="text-gray-400 text-xs uppercase tracking-wide">Registration ID</dt>
                        <dd className="text-gray-600 font-mono text-xs mt-0.5 break-all">{reg.id}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-400 text-xs uppercase tracking-wide">Emails Sent</dt>
                        <dd className="text-gray-800 mt-0.5">
                          {reg.customer_email_sent ? '✓ Customer' : '✗ Customer'}
                          {' · '}
                          {reg.admin_email_sent ? '✓ Admin' : '✗ Admin'}
                        </dd>
                      </div>
                    </dl>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
